/**
 * Tipos e wrappers das Cloud Functions Callable.
 * Sempre tipar os payloads dos lados aqui e em backend/functions/src/index.ts.
 */
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'

// ---------- Tipos compartilhados (espelham backend/functions) ----------

export interface CriarTenantInput {
  razao_social: string
  nome_fantasia: string
  email: string
  telefone?: string
  cnpj?: string
  plan_id: 'basico' | 'intermediario' | 'completo'
  addons?: string[]
  trial_dias?: number
}

export interface CriarTenantOutput {
  tenantId: string
  ownerUid: string
  reset_link: string
}

export interface AtualizarPlanoInput {
  tenantId: string
  plan_id: 'basico' | 'intermediario' | 'completo'
  addons?: string[]
  expira_em?: string
}

export interface BloquearTenantInput {
  tenantId: string
  motivo?: string
}

export interface CriarCheckoutInput {
  plan_id: 'basico' | 'intermediario' | 'completo'
  addons?: string[]
  email: string
  nome: string
  telefone?: string
  gateway: 'stripe' | 'mercadopago'
}

export interface CriarCheckoutOutput {
  url: string
}

export interface EnviarWhatsAppInput {
  tenantId: string
  to: string
  template?: string
  template_params?: string[]
  text?: string
}

// ---------- Callables ----------

export const criarTenant = httpsCallable<CriarTenantInput, CriarTenantOutput>(functions, 'criarTenant')
export const atualizarPlano = httpsCallable<AtualizarPlanoInput, { ok: true }>(functions, 'atualizarPlano')

// ---------- Templates de profissão ----------

export type ProfessionId =
  | 'cerimonial' | 'dj' | 'decorador' | 'buffet' | 'salao' | 'eventos_completo'

export interface AplicarTemplateInput {
  tenantId: string
  profession: ProfessionId
  replace?: boolean
}

export interface TemplateMeta {
  id: ProfessionId
  nome: string
  descricao: string
  icone: string
  default_color: string
  modules_count: number
  sample_vocab: { events: string; clients: string }
}

export const aplicarTemplateProfissao = httpsCallable<
  AplicarTemplateInput,
  { ok: true; profession: ProfessionId; nome: string; modules_applied: string[] }
>(functions, 'aplicarTemplateProfissao')

export const listarTemplatesProfissao = httpsCallable<
  Record<string, never>,
  { templates: TemplateMeta[] }
>(functions, 'listarTemplatesProfissao')
export const bloquearTenant = httpsCallable<BloquearTenantInput, { ok: true }>(functions, 'bloquearTenant')
export const desbloquearTenant = httpsCallable<{ tenantId: string }, { ok: true }>(functions, 'desbloquearTenant')

// ---------- Histórico oculto (a partir de 01/01/2027) ----------
// 'login' = entrou com senha; 'abertura' = abriu o sistema com a sessão salva.
// suporteTenantId só é aceito pelo servidor quando quem chama é master.
export const registrarAcesso = httpsCallable<
  { forma: 'login' | 'abertura'; suporteTenantId?: string | null },
  { ok: true; registrado: boolean }
>(functions, 'registrarAcesso')

// ---------- Lixeira de clientes (só master) ----------
// Fluxo em duas etapas: arquivar (reversível) → excluir definitivo (irreversível,
// com backup automático). Ver backend/functions/src/auth/excluirTenant.ts.

export const arquivarTenant = httpsCallable<
  { tenantId: string; motivo?: string },
  { ok: true; contas_desativadas: number }
>(functions, 'arquivarTenant')

export const restaurarTenant = httpsCallable<
  { tenantId: string },
  { ok: true; contas_reativadas: number; status: string }
>(functions, 'restaurarTenant')

export const excluirTenantDefinitivo = httpsCallable<
  { tenantId: string; confirmacao: string },
  {
    ok: true
    backup_path: string
    backup_bytes: number
    links_publicos_removidos: Record<string, number>
    arquivos_storage_removidos: number
    contas_desativadas: number
  }
>(functions, 'excluirTenantDefinitivo')

export const baixarBackupExcluido = httpsCallable<
  { tenantId: string },
  { filename: string; content: string }
>(functions, 'baixarBackupExcluido')
export const criarCheckout = httpsCallable<CriarCheckoutInput, CriarCheckoutOutput>(functions, 'criarCheckout')
export const enviarWhatsApp = httpsCallable<EnviarWhatsAppInput, { ok: true; message_id: string }>(functions, 'enviarWhatsApp')

// ---------- Z-API (alternativa à Meta Cloud API) ----------

export interface ConfigurarZAPIInput {
  tenantId: string
  instance_id: string
  token: string
  client_token?: string
  phone_number?: string
  testar_conexao?: boolean
}
export interface ConfigurarZAPIOutput {
  ok: true
  connected: boolean | null
  webhook_url: string
}

export interface StatusZAPIInput { tenantId: string }
export type StatusZAPIOutput =
  | { configured: false }
  | { configured: true; connected: true; phone_number: string | null; session?: string }
  | { configured: true; connected: false; qr_code_base64: string | null; error?: string }

export interface EnviarZAPIInput {
  tenantId: string
  to: string
  type?: 'text' | 'image' | 'document' | 'buttons'
  text?: string
  image_url?: string
  document_url?: string
  document_name?: string
  document_extension?: string
  caption?: string
  buttons?: { id: string; label: string }[]
  client_id?: string
  event_id?: string
}

export const configurarZAPI = httpsCallable<ConfigurarZAPIInput, ConfigurarZAPIOutput>(functions, 'configurarZAPI')
export const statusZAPI = httpsCallable<StatusZAPIInput, StatusZAPIOutput>(functions, 'statusZAPI')
export const enviarZAPI = httpsCallable<EnviarZAPIInput, { ok: true; message_id: string }>(functions, 'enviarZAPI')
export const gerarContratoPDF = httpsCallable<{ contractId: string }, { pdf_url: string }>(functions, 'gerarContratoPDF')
export const exportarBackup = httpsCallable<{ tenantId: string }, { filename: string; content: string }>(functions, 'exportarBackup')
export const entrarComoSuporte = httpsCallable<{ tenantId: string; motivo: string }, { token: string }>(functions, 'entrarComoSuporte')

// ---------- Import de backup (restauração) ----------
export type ImportItemStatus = 'new' | 'duplicate' | 'similar'
export interface ImportItemDiff { field: string; incoming: string; existing: string }
export interface ImportAnalyzedItem {
  index: number
  status: ImportItemStatus
  label: string
  matchId?: string
  diff?: ImportItemDiff[]
}
export interface ImportColReport {
  key: string
  total: number
  novos: number
  duplicatas: number
  parecidos: number
  items: ImportAnalyzedItem[]
}
export type ImportDecision = 'create' | 'replace' | 'skip'
export interface ImportarBackupInput {
  tenantId: string
  mode: 'analyze' | 'apply'
  data: Record<string, unknown>
  decisions?: Record<string, ImportDecision>
}
export type ImportarBackupOutput =
  | { mode: 'analyze'; resumo: { novos: number; duplicatas: number; parecidos: number }; reports: ImportColReport[] }
  | { mode: 'apply'; criados: number; substituidos: number; ignorados: number }

export const importarBackup = httpsCallable<ImportarBackupInput, ImportarBackupOutput>(functions, 'importarBackup')

// ---------- Suporte (master) ----------
export const baixarBackup = httpsCallable<
  { tenantId: string; backupId: string },
  { filename: string; content: string }
>(functions, 'baixarBackup')

export const resetarSenhaCliente = httpsCallable<
  { tenantId: string; uid: string },
  { reset_link: string; email: string }
>(functions, 'resetarSenhaCliente')

export const alterarEmailCliente = httpsCallable<
  { tenantId: string; uid: string; novoEmail: string },
  { ok: true; email: string }
>(functions, 'alterarEmailCliente')
